/* global BigInt */
import { useEffect, useState } from 'react';
import { usePublicClient } from 'wagmi';
import { activeChain } from './chains';

const POLL_MS = 5000;

// Why this file exists.
//
// Robinhood Chain is EIP-1559 but reports a suggested priority fee of 0, and the
// median tip actually paid on-chain is 0. That is harmless while blocks have
// room. During the mint rush every block ran at 100% capacity -- and when every
// pending transaction bids the same zero tip there is nothing for the builder to
// order by, so wallets sit unconfirmed and users read it as "minting is broken".
//
// MetaMask compounds it: with no gas oracle for chain 4663 and a zero suggested
// tip, it renders "Network fee: Unavailable" and warns on the transaction, which
// stops people signing at all.
//
// Supplying explicit fee parameters fixes both: the wallet has a concrete number
// to display, and the transaction carries a real tip so it gets ordered.
const MIN_TIP_WEI = 50000000n; // 0.05 gwei -- ~$0.01 at this chain's gas costs

// maxFeePerGas is a CEILING, not a payment: the sender only ever pays
// baseFee + tip. Headroom therefore costs nothing and is precisely what stops a
// transaction stranding when the base fee climbs mid-rush.
const BASE_FEE_HEADROOM = 4n;

// Measured on mainnet against the live contract: gas is linear in quantity.
//   qty 1 -> 143,122     qty 3 -> 200,062
//   qty 2 -> 171,592     qty 5 -> 257,002
export const GAS_BASE = 114652n;
export const GAS_PER_TOKEN = 28470n;

export function estimateMintGas(quantity) {
    return GAS_BASE + GAS_PER_TOKEN * BigInt(quantity);
}

/**
 * Polls the chain's live fee market and returns transaction fee parameters.
 * Returns null while unavailable -- callers must then omit fee overrides and
 * let the wallet decide, rather than sending zeroes.
 */
export function useGasFees() {
    const publicClient = usePublicClient({ chainId: activeChain.id });
    const [fees, setFees] = useState(null);

    useEffect(() => {
        if (!publicClient) return undefined;
        let cancelled = false;

        const read = async () => {
            try {
                const block = await publicClient.getBlock({ blockTag: 'latest' });
                const baseFee = block.baseFeePerGas;
                // Pre-1559 chain, or an RPC that omits it: fall back to wallet
                // estimation rather than inventing numbers.
                if (baseFee === null || baseFee === undefined) {
                    if (!cancelled) setFees(null);
                    return;
                }

                let tip = MIN_TIP_WEI;
                try {
                    const suggested = await publicClient.estimateMaxPriorityFeePerGas();
                    if (suggested > tip) tip = suggested;
                } catch (e) {
                    // Not all RPCs implement it; the floor above still applies.
                }

                if (cancelled) return;
                setFees({
                    baseFeePerGas: baseFee,
                    maxPriorityFeePerGas: tip,
                    maxFeePerGas: baseFee * BASE_FEE_HEADROOM + tip,
                    // What the transaction will actually cost per gas unit.
                    effectivePerGas: baseFee + tip,
                });
            } catch (e) {
                if (!cancelled) setFees(null);
            }
        };

        read();
        const id = setInterval(read, POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [publicClient]);

    return fees;
}

/** Fee params spread onto a write call; empty when the poll has no data. */
export function feeOverrides(fees) {
    if (!fees) return {};
    return {
        maxFeePerGas: fees.maxFeePerGas,
        maxPriorityFeePerGas: fees.maxPriorityFeePerGas,
    };
}
